// Batch 45 (doc 45): the page side of the official Synova support widget.
//
// server.js writes the loader into the served HTML, once, and only with a valid
// website key (see serverSynova.js). This module loads nothing. It keeps the
// native floating button and the app in step with the widget's real state:
//
//   - The button stays hidden until the iframe announces SYNOVA_WIDGET_READY. The
//     loader shows it on a 4 s timer whether or not the iframe started, and the
//     iframe drops every message that arrives before it is initialised. An open
//     at that moment leaves the parent "open" over a transparent frame that
//     draws nothing and takes every click; on a phone that is the whole screen,
//     with the body locked and no button left. Reproduced on Staging, doc 45.
//   - open() only after readiness, and never on an open widget: a second open()
//     records the locked body styles as the ones to restore on close.
//   - The phone scroll position survives the panel. The loader reads it after
//     it has fixed the body, so it stores 0 and a closed panel lands on the top
//     of the page.
//   - A window that crosses 768 px with the panel open (a rotated iPad, whose
//     Safari reports a Mac) ends the phone lock too: the loader judges "phone"
//     again on close and would leave the body fixed and the button hidden.
//   - No private student data reaches the widget, here or in serverSynova.js.
const WIDGET_ORIGIN = 'https://wg.synova.ir'
const LOADER_SELECTOR = `script[src^="${WIDGET_ORIGIN}/loader.js"]`
const BUTTON_SELECTOR = '#synova-floating-button'
const READY_CLASS = 'borna-synova-ready'
const ALLOWED_CLASS = 'borna-synova-allowed'
const RAISED_CLASS = 'borna-synova-raised'
const READY_POLL_MS = 100
const READY_POLLS = 100
const CLOSE_POLL_MS = 500

let listening = false
let launcherAllowed = false
let scrollBeforeOpen = null

// Only the whole API counts: this runs before the app starts, so a loader build
// that lacks part of it must read as "no widget", never throw.
function widget() {
  const w = window.SynovaWidget
  const complete =
    w &&
    typeof w.open === 'function' &&
    typeof w.isOpen === 'function' &&
    typeof w.isReady === 'function'
  return complete ? w : null
}

function markReady() {
  document.documentElement.classList.add(READY_CLASS)
}

function rememberScroll() {
  scrollBeforeOpen = window.scrollY
}

// Runs ahead of the loader's own close handler (main.js registers it before the
// deferred loader), so the body is still fixed here on a phone. The timeout runs
// after the loader has finished.
function afterPanelClose() {
  const y = scrollBeforeOpen
  scrollBeforeOpen = null
  const phoneLock = document.body.style.position === 'fixed'
  setTimeout(() => {
    const w = widget()
    if (!w || w.isOpen()) return
    const body = document.body.style
    if (body.position === 'fixed') {
      body.position = ''
      body.overflow = ''
      body.top = ''
      body.width = ''
      w.showButton()
    }
    if (phoneLock && y !== null) window.scrollTo(0, y)
  })
}

// main.js calls this once, before anything is awaited: the iframe's READY comes
// once, and can come before the app has finished starting.
export function watchSynovaReady() {
  if (listening) return
  listening = true
  window.addEventListener('message', event => {
    if (event.origin !== WIDGET_ORIGIN || !event.data) return
    if (event.data.type === 'SYNOVA_WIDGET_READY') markReady()
    if (event.data.type === 'SYNOVA_WIDGET_CLOSE') afterPanelClose()
  })
  document.addEventListener(
    'click',
    event => {
      const target = event.target
      if (target && target.closest && target.closest(BUTTON_SELECTOR)) {
        rememberScroll()
      }
    },
    true
  )
  const w = widget()
  if (w && w.isReady()) markReady()
}

// Whether this user gets the launcher at all (see `synovaLauncherAllowed` in
// App/index.vue).
export function setLauncherAllowed(allowed) {
  launcherAllowed = !!allowed
  document.documentElement.classList.toggle(ALLOWED_CLASS, launcherAllowed)
}

// On a desktop the button is the panel's only close control, so whoever opens
// the panel needs it, launcher or not, until the panel is closed.
function showLauncherWhileOpen(w) {
  if (launcherAllowed) return
  document.documentElement.classList.add(ALLOWED_CLASS)
  const watch = setInterval(() => {
    if (w.isOpen()) return
    clearInterval(watch)
    document.documentElement.classList.toggle(ALLOWED_CLASS, launcherAllowed)
  }, CLOSE_POLL_MS)
}

// Whether this route needs the phone button above its docked composer.
export function setLauncherRaised(raised) {
  document.documentElement.classList.toggle(RAISED_CLASS, !!raised)
}

// Resolves with the ready widget, or null when there is none to wait for.
function whenReady() {
  return new Promise(resolve => {
    let polls = 0
    const check = () => {
      if (!document.querySelector(LOADER_SELECTOR)) return resolve(null)
      const w = widget()
      if (w && w.isReady()) return resolve(w)
      polls += 1
      if (polls >= READY_POLLS) return resolve(null)
      setTimeout(check, READY_POLL_MS)
    }
    check()
  })
}

// `/contact`: opens the support chat. Resolves true when it is open, false when
// Synova is not reachable (the caller shows the notice); it never rejects.
export function openSupport() {
  return whenReady().then(w => {
    if (!w) return false
    try {
      if (!w.isOpen()) {
        rememberScroll()
        w.open()
        showLauncherWhileOpen(w)
      }
      return true
    } catch (err) {
      return false
    }
  })
}

// Test seam: forgets the shared state so each test starts from a clean page.
export function __resetSynovaForTests() {
  listening = false
  launcherAllowed = false
  scrollBeforeOpen = null
  document.documentElement.classList.remove(
    READY_CLASS,
    ALLOWED_CLASS,
    RAISED_CLASS
  )
  delete window.SynovaWidget
}
